import { useEffect, useState } from 'react';

/**
 * The small bordered Copy button. Button-only, unlike `components/copy.jsx`,
 * which also renders the text: here the caller owns the layout.
 *
 * `what` names the thing copied, so a column of these reads as distinct
 * buttons to a screen reader instead of four identical "Copy"s.
 */
export function CopyButton({ text, what, label = 'Copy', className = '' }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(t);
  }, [copied]);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      // A denied clipboard leaves the label as it was; claiming "Copied"
      // for text that never arrived would be worse than saying nothing.
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={what ? `Copy ${what}` : undefined}
      className={
        'shrink-0 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-800 dark:bg-gray-900 ' +
        'text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 dark:text-gray-50 dark:hover:bg-gray-800 ' +
        'focus-visible:ring-blue-600 ' + className
      }
    >
      <span aria-live="polite">{copied ? 'Copied' : label}</span>
    </button>
  );
}
